import { useCallback, useEffect } from "react"
import { pipe } from "fp-ts/function"
import * as E from "fp-ts/Either"
import fetchClientBookShelfAnalyzePolling, {
  type BookShelfAnalyzePollingResponse,
} from "@/app/(tabs)/bookshelf/_fetch/fetchClientBookShelfAnalyzePolling"
import { useBookshelfExtractStore } from "../_store/useBookshelfExtractStore"
import { mockBooks } from "../_data/mockBookData"

interface UseBookshelfAnalysisProps {
  analysisId?: string
  removeProcessedId?: (analysisId: string, reason?: string) => void
}

/**
 * 책장 분석 데이터 관리 Hook
 * API 호출, 폴링, 에러 처리를 담당
 */
export const useBookshelfAnalysis = ({
  analysisId,
  removeProcessedId,
}: UseBookshelfAnalysisProps) => {
  const {
    setLoading,
    setError,
    setPollingBooks,
    setAnalysisStatus,
    resetState,
    pollingBooks,
    isLoading,
    error,
    analysisStatus,
  } = useBookshelfExtractStore()

  // API 호출 함수
  const fetchAnalysisData = useCallback(async () => {
    if (!analysisId) {
      setAnalysisStatus("empty")
      return
    }

    try {
      setError(null)
      setAnalysisStatus("loading")
      console.log(`[BookshelfAnalysis] 분석 데이터 요청: ${analysisId}`)

      const pollingResult = await fetchClientBookShelfAnalyzePolling(analysisId)

      pipe(
        pollingResult,
        E.fold(
          (error) => {
            console.error("[BookshelfAnalysis] API 에러:", error)
            let errorMessage = "API 호출 중 오류가 발생했습니다."
            
            try {
              if (error && typeof error === "object" && "message" in error) {
                const messageValue = (error as Record<string, unknown>).message
                if (typeof messageValue === "string") {
                  errorMessage = messageValue
                }
              }
            } catch {
              // 타입 에러 시 기본 메시지 사용
            }

            setError(errorMessage)
            setAnalysisStatus("error")

            // API 실패 시 처리된 ID에서 제거하여 재시도 가능하도록 함
            if (removeProcessedId) {
              removeProcessedId(analysisId, "api-polling-failure")
            }
          },
          (books: BookShelfAnalyzePollingResponse) => {
            if (books && books.length > 0) {
              setPollingBooks(books)
              console.log(`[BookshelfAnalysis] 분석 완료: ${books.length}권`)
            } else {
              console.log("[BookshelfAnalysis] 분석 결과 없음 - Mock 데이터 사용")
              setAnalysisStatus("empty")
            }
          }
        )
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "알 수 없는 오류"
      console.error("[BookshelfAnalysis] 예외 발생:", err)
      setError(errorMessage)
      setAnalysisStatus("error")

      // 예외 발생 시 처리된 ID에서 제거하여 재시도 가능하도록 함
      if (removeProcessedId) {
        removeProcessedId(analysisId, "api-polling-exception")
      }
    }
  }, [analysisId, removeProcessedId, setError, setPollingBooks, setAnalysisStatus])

  // 분석 데이터 가져오기 (analysisId 변경 시에만 실행)
  useEffect(() => {
    if (!analysisId) {
      resetState()
      setAnalysisStatus("empty")
      return
    }

    console.log(`[BookshelfAnalysis] 분석 데이터 요청 시작: ${analysisId}`)
    setLoading(true)
    fetchAnalysisData()
  }, [analysisId, fetchAnalysisData, resetState, setLoading, setAnalysisStatus])

  // 실제 사용할 도서 목록 (폴링 결과가 있으면 사용하고, 없으면 mock 데이터 사용)
  const books = pollingBooks.length > 0 ? pollingBooks : mockBooks

  return {
    books,
    isLoading,
    error,
    analysisStatus,
    refetch: fetchAnalysisData,
  }
}